import { createContext, PropsWithChildren, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { ProjectUpdateDto } from '../../_generated/api/structure'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useStructureContext } from './StructureContext'
import { useMutableObject } from '../../hooks/mutable'
import { useProvideContext } from '../../hooks/context'
import { useToastContext } from './ToastContext'
import Project from '../../models/Project'
import { StructureTypes } from '../../models/Structure'
import { mapReadResponse, throwIfResponseNotOk } from '../../tools/api'
import { useAction } from '../../hooks/action'
import { useQR } from '../../hooks/qr'

export interface ProjectContextValue extends DataContextValue {
    qr?: string
    qrLoading: boolean
    qrError: Error | undefined

    selectedProject?: Project
    selectedProjectLoading: boolean
    updateProject: (change: ProjectUpdateDto) => Promise<Project | undefined>
    selectedProjectError: Error | undefined
    refreshSelectedProject: () => void
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const params = useParams()
    const { projectApi } = useApiContext()
    const { selectedOrganization, lazySelectedOrganizationId } = useUserContext()
    const { structure, updateStructureByImpactDto } = useStructureContext()
    const Toast = useToastContext()
    const projectId = params.projectId

    const [selectedProject, selectedProjectLoading, selectedProjectError, refreshSelectedProject] =
        useMutableObject(
            async () => {
                const orgId = selectedOrganization?.id
                if (!(orgId && projectId)) return
                const response = await projectApi.getProjectV3({
                    projectId,
                    tenantId: orgId,
                    includeArchived: true,
                })
                return mapReadResponse(response, Project)
            },
            [
                projectId,
                selectedOrganization?.id,
                // Depend only on the structure's existence rather than its state.
                !structure.empty,
            ],
            true
        )

    const [_updateLoading, updateError, updateProject] = useAction(
        async (change: ProjectUpdateDto) => {
            if (!projectId) return
            const response = await projectApi.updateProjectV3({
                projectId,
                tenantId: lazySelectedOrganizationId,
                projectUpdateDto: change,
            })
            throwIfResponseNotOk(response)
            updateStructureByImpactDto(response.data)
            refreshSelectedProject()

            Toast.completed('update', 'project')
            return new Project(response.data.model)
        }
    )

    const [qr, qrLoading, qrError] = useQR(StructureTypes.Project, selectedProject?.id)

    const loading = selectedProjectLoading || qrLoading
    const error = updateError || qrError

    useEffect(() => {
        if (error) Toast.error(error, 'Project error')
    }, [error])

    const value: ProjectContextValue = {
        loading,
        error,

        qr,
        qrLoading,
        qrError,

        selectedProject,
        selectedProjectLoading,
        updateProject,
        refreshSelectedProject,
        selectedProjectError,
    }

    return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
}

export const useProjectContext = () => useProvideContext(ProjectContext, 'Project')
